'use client';

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, CreditCard, ArrowRight, Clock, DollarSign, TrendingUp } from 'lucide-react';
import { useAuth } from '@/providers/auth-provider';
import { useWallet } from '@/hooks/useWallet';
import { useNumbers } from '@/hooks/useNumbers';
import { useRealtime } from '@/providers/realtime-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, cn } from '@/lib/utils';

const statCards = [
  { name: 'Total Balance', icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/10' },
  { name: 'This Month', icon: TrendingUp, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { name: 'Active Numbers', icon: Smartphone, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  { name: 'Pending Withdrawals', icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
];

export function DashboardPage() {
  const { user } = useAuth();
  const { balance, transactions, loading: walletLoading, fetchBalance, fetchTransactions } = useWallet();
  const { assignments, loading: numbersLoading, fetchAssignments } = useNumbers();
  const { isConnected } = useRealtime();

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
    fetchAssignments();
  }, [fetchBalance, fetchTransactions, fetchAssignments]);

  const activeAssignments = assignments.filter(a => a.status === 'active');
  const totalRewards = balance?.buckets.approved || 0;
  const thisMonthRewards = transactions
    .filter(t => t.type === 'reward' && new Date(t.createdAt).getMonth() === new Date().getMonth())
    .reduce((sum, t) => sum + t.amount, 0);

  const stats = [
    { value: formatCurrency(totalRewards), label: 'Total Balance' },
    { value: formatCurrency(thisMonthRewards), label: 'This Month' },
    { value: activeAssignments.length.toString(), label: 'Active Numbers' },
    { value: balance ? formatCurrency(balance.buckets.pending) : '$0.00', label: 'Pending Rewards' },
  ];

  const recentActivity = [
    ...transactions.slice(0, 5).map(t => ({
      id: t.id,
      type: 'transaction',
      title: `${t.type.charAt(0).toUpperCase() + t.type.slice(1)}: ${formatCurrency(t.amount)}`,
      description: t.description,
      time: t.createdAt,
      status: t.status,
    })),
    ...assignments.slice(0, 3).map(a => ({
      id: a.id,
      type: 'assignment',
      title: `Number ${a.phoneNumber} ${a.status}`,
      description: a.status === 'active' ? 'Earning rewards' : 'Not active',
      time: a.assignedAt,
      status: a.status,
    })),
  ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).slice(0, 5);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
      case 'active':
        return <Badge className="bg-green-500/10 text-green-400">{status}</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/10 text-amber-400">{status}</Badge>;
      case 'rejected':
      case 'failed':
      case 'expired':
      case 'revoked':
        return <Badge className="bg-red-500/10 text-red-400">{status}</Badge>;
      default:
        return <Badge className="bg-slate-500/10 text-slate-400">{status}</Badge>;
    }
  };

  if (walletLoading || numbersLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map(() => (
            <Card key={Math.random()} className="glass">
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-1/4 mb-2" />
                <Skeleton className="h-8 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="glass">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome back, {user?.firstName}! Here's your overview.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn('flex items-center gap-1.5 px-3 py-1 rounded-full text-xs', isConnected ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400')}>
            <span className={cn('w-1.5 h-1.5 rounded-full', isConnected ? 'bg-green-400' : 'bg-amber-400')} />
            {isConnected ? 'Live' : 'Connecting'}
          </span>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = statCards[index].icon;
          return (
            <Card key={index} className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">{stat.label}</p>
                    <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                  </div>
                  <div className={cn('p-3 rounded-xl', statCards[index].bg)}>
                    <Icon className={cn('h-6 w-6', statCards[index].color)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Wallet Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {balance && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Withdrawable</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(balance.buckets.withdrawable)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Pending</p>
                    <p className="text-2xl font-bold text-amber-400">{formatCurrency(balance.buckets.pending)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Approved</p>
                    <p className="text-2xl font-bold text-blue-400">{formatCurrency(balance.buckets.approved)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Frozen</p>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(balance.buckets.frozen)}</p>
                  </div>
                </div>
                <Link to="/wallet" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
                  View Details <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle>Active Numbers</CardTitle>
          </CardHeader>
          <CardContent>
            {activeAssignments.length > 0 ? (
              <div className="space-y-3">
                {activeAssignments.slice(0, 5).map(assignment => (
                  <div key={assignment.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500/10 rounded-lg">
                        <Smartphone className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{assignment.phoneNumber}</p>
                        <p className="text-sm text-slate-400">${assignment.monthlyReward}/mo</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(assignment.status)}
                      <p className="text-xs text-slate-500 mt-1">Expires {new Date(assignment.expiresAt!).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
                <Link to="/numbers" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
                  View All <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <Smartphone className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No active numbers</p>
                <Link to="/numbers" className="inline-flex items-center gap-2 mt-4 text-cyan-400 hover:text-cyan-300">
                  Get a Number <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length > 0 ? (
            <div className="space-y-3">
              {recentActivity.map(activity => (
                <div key={activity.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className={cn('p-2 rounded-lg', activity.type === 'transaction' ? 'bg-cyan-500/10' : 'bg-purple-500/10')}>
                      {activity.type === 'transaction' ? (
                        <CreditCard className="h-5 w-5 text-cyan-400" />
                      ) : (
                        <Smartphone className="h-5 w-5 text-purple-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-white">{activity.title}</p>
                      <p className="text-sm text-slate-400">{activity.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(activity.status)}
                    <p className="text-xs text-slate-500 mt-1">{new Date(activity.time).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              No recent activity
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}